import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Separator,
} from "@roberttamas/ui";
import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Copy } from "@/components/icons";
import { InfoNote } from "@/components/info-note";
import { formatDate } from "@/lib/locale-format";
import { formatAmount } from "@/lib/pricing";
import type { OrderResp } from "@/lib/types";

function CopyRow({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t("payment.copiedToast"));
    } catch {
      toast.error(t("payment.copyFailed"));
    }
  };
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs uppercase text-muted-foreground">{label}</p>
        <p className="truncate font-mono text-sm">{value}</p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => void copy()}
        aria-label={t("payment.copyAria", { label })}
      >
        <Copy className="h-4 w-4" />
      </Button>
    </div>
  );
}

/**
 * QR Platba bank-transfer card for an order awaiting payment. Requires
 * `order.payment` to be present.
 */
export function PaymentCard({ order }: { order: OrderResp }) {
  const { t } = useTranslation();
  const { snapshot, payment } = order;
  if (!payment) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t("payment.title", { amount: formatAmount(snapshot.amountMinor, snapshot.currency) })}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start">
          <div className="shrink-0 rounded-lg border bg-white p-4">
            <QRCodeSVG value={payment.spayd} size={208} level="M" />
          </div>
          <div className="w-full min-w-0 space-y-3">
            <CopyRow
              label={t("payment.accountIban")}
              value={payment.readableAccount ?? payment.iban}
            />
            <Separator />
            <CopyRow label={t("payment.variableSymbol")} value={payment.variableSymbol} />
            <Separator />
            <CopyRow
              label={t("payment.amount")}
              value={formatAmount(snapshot.amountMinor, snapshot.currency)}
            />
            {payment.dueDate && (
              <>
                <Separator />
                <div>
                  <p className="text-xs uppercase text-muted-foreground">
                    {t("payment.dueDate")}
                  </p>
                  <p className="text-sm">{formatDate(new Date(payment.dueDate))}</p>
                </div>
              </>
            )}
          </div>
        </div>

        <InfoNote>{t("payment.note", { email: order.customer.email })}</InfoNote>
      </CardContent>
    </Card>
  );
}
